/**
 * /university/marketplace — published-skill marketplace browser.
 *
 * Phase 9 (M7) replaces the thin Phase-8 internal listing with the full
 * browseable surface. Search + category filter + sort. Each row links to
 * /university/marketplace/$skillId for detail + install.
 *
 * Founders see a "View all" toggle that surfaces non-published rows
 * (candidate/private/archived) for housekeeping. Other authenticated
 * roles are limited to the public catalog.
 */
import type { MarketplaceSkill } from '@chaos/types/learning';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ArrowDown,
  ArrowDownAZ,
  ArrowUpAZ,
  ChevronLeft,
  Download,
  Loader2,
  Search,
  Sparkles,
  Star,
} from 'lucide-react';
import * as React from 'react';

import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  effectivenessTone,
  fmtCount,
  fmtRelative,
  fmtScore,
  MARKETPLACE_STATUS_LABEL,
  TONE_DOT_CLASS,
  TONE_TEXT_CLASS,
} from '../../../../components/university/formatters';
import { learningApi, learningKeys } from '../../../../lib/api/learning';
import { cn } from '../../../../lib/utils';
import { useAuthStore } from '../../../../stores/auth-store';

type SortOption = 'updated' | 'installs' | 'effectiveness' | 'name';

const SORT_LABEL: Record<SortOption, string> = {
  updated: 'Recently updated',
  installs: 'Most installed',
  effectiveness: 'Highest score',
  name: 'Name (A→Z)',
};

function MarketplaceIndexRoute() {
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const role =
    workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.membership.role ?? null;
  const isFounder = role === 'founder' || role === 'admin';

  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [category, setCategory] = React.useState<string | null>(null);
  const [sort, setSort] = React.useState<SortOption>('updated');
  const [includeAll, setIncludeAll] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 220);
    return () => clearTimeout(t);
  }, [query]);

  const params = React.useMemo(() => {
    const p: {
      q?: string;
      category?: string;
      sort: SortOption;
      includeAllStatuses?: boolean;
    } = { sort };
    if (debouncedQuery) p.q = debouncedQuery;
    if (category) p.category = category;
    if (includeAll && isFounder) p.includeAllStatuses = true;
    return p;
  }, [debouncedQuery, category, sort, includeAll, isFounder]);

  const listQ = useQuery({
    queryKey: learningKeys.marketplaceSkills(
      params as Record<string, string | number | boolean | undefined>,
    ),
    queryFn: () => learningApi.listMarketplaceSkills(params),
    staleTime: 30_000,
  });

  const skills = listQ.data?.skills ?? [];
  const categories = React.useMemo(() => {
    const set = new Set<string>();
    for (const s of skills) {
      if (s.category) set.add(s.category);
    }
    return Array.from(set).sort();
  }, [skills]);

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-4 p-6">
      <header className="flex flex-col gap-1">
        <Link
          to="/university"
          className="inline-flex w-fit items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" aria-hidden /> Back to University
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-[16px] font-semibold tracking-tight text-foreground">
            Marketplace
            <span className="ml-2 text-[12px] font-normal text-muted-foreground">
              {skills.length} {skills.length === 1 ? 'skill' : 'skills'}
            </span>
          </h1>
          {listQ.isFetching ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> syncing…
            </span>
          ) : null}
        </div>
        <p className="text-[12px] text-muted-foreground">
          Browse published skills. Install into your workspace to iterate locally. Founders can
          publish their own skills (with redaction preview).
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3 rounded border border-border bg-background p-3">
        <div className="flex min-w-[220px] flex-1 flex-col gap-1">
          <Label
            htmlFor="mp-search"
            className="text-[11px] uppercase tracking-wider text-muted-foreground"
          >
            Search
          </Label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="mp-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="By name, key or description…"
              className="h-9 pl-7 text-[13px]"
            />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label
            htmlFor="mp-category"
            className="text-[11px] uppercase tracking-wider text-muted-foreground"
          >
            Category
          </Label>
          <select
            id="mp-category"
            value={category ?? 'all'}
            onChange={(e) => setCategory(e.target.value === 'all' ? null : e.target.value)}
            className={cn(
              'h-9 rounded border border-border bg-background px-2 text-[13px]',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
            )}
          >
            <option value="all">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <Label
            htmlFor="mp-sort"
            className="text-[11px] uppercase tracking-wider text-muted-foreground"
          >
            Sort by
          </Label>
          <select
            id="mp-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            className={cn(
              'h-9 rounded border border-border bg-background px-2 text-[13px]',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
            )}
          >
            {(Object.keys(SORT_LABEL) as SortOption[]).map((opt) => (
              <option key={opt} value={opt}>
                {SORT_LABEL[opt]}
              </option>
            ))}
          </select>
        </div>
        {isFounder ? (
          <label className="ml-auto flex cursor-pointer items-center gap-2 text-[12px] text-muted-foreground">
            <input
              type="checkbox"
              checked={includeAll}
              onChange={(e) => setIncludeAll(e.target.checked)}
              aria-label="Include non-published"
            />
            View all (founder)
          </label>
        ) : null}
      </div>

      {listQ.isPending ? (
        <div className="px-2 py-4 text-[12px] text-muted-foreground">Loading marketplace…</div>
      ) : listQ.isError ? (
        <div className="rounded border border-destructive/50 bg-destructive/5 p-3 text-[12px] text-destructive">
          Failed to load marketplace: {(listQ.error as Error).message}
        </div>
      ) : skills.length === 0 ? (
        <div className="px-2 py-12 text-center text-[12px] text-muted-foreground/70">
          {debouncedQuery ? 'No skills match this search.' : 'No published skills yet.'}
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-2 lg:grid-cols-2">
          {skills.map((s) => (
            <MarketplaceSkillRow key={s.skillId} skill={s} />
          ))}
        </ul>
      )}
    </section>
  );
}

function MarketplaceSkillRow({ skill }: { skill: MarketplaceSkill }) {
  const tone = effectivenessTone(skill.effectivenessScore);
  const statusLabel = MARKETPLACE_STATUS_LABEL[skill.marketplaceStatus] ?? skill.marketplaceStatus;
  return (
    <li className="group/listing relative flex flex-col gap-1.5 rounded border border-border bg-background p-3 transition-colors hover:bg-muted/40">
      <Link
        to="/university/marketplace/$skillId"
        params={{ skillId: skill.skillId }}
        className="absolute inset-0 z-0 rounded"
        aria-label={`Open ${skill.name}`}
      />
      <div className="relative z-10 flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT_CLASS[tone])}
            />
            <span className="truncate text-[13px] font-semibold text-foreground">{skill.name}</span>
            <span className="rounded border border-border bg-muted/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
              {statusLabel}
            </span>
          </div>
          <span className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">
            {skill.key} · v{skill.version}
          </span>
          {skill.description ? (
            <p className="mt-1 line-clamp-2 text-[12px] text-muted-foreground">
              {skill.description}
            </p>
          ) : null}
        </div>
      </div>
      <div className="relative z-10 mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <Sparkles className="h-3 w-3" aria-hidden />
          <span className={cn('font-mono font-medium tabular-nums', TONE_TEXT_CLASS[tone])}>
            {fmtScore(skill.effectivenessScore)}
          </span>
        </span>
        <span className="inline-flex items-center gap-1">
          <Download className="h-3 w-3" aria-hidden />
          <span className="font-mono font-medium tabular-nums text-foreground">
            {fmtCount(skill.installCount)}
          </span>
          installs
        </span>
        <span className="inline-flex items-center gap-1">
          <Star className="h-3 w-3" aria-hidden />
          <span className="font-mono font-medium tabular-nums text-foreground">
            {fmtCount(skill.exportCount)}
          </span>
          exports
        </span>
        {skill.publishedAt ? (
          <span className="ml-auto">published {fmtRelative(skill.publishedAt)}</span>
        ) : skill.lastExportAt ? (
          <span className="ml-auto">updated {fmtRelative(skill.lastExportAt)}</span>
        ) : null}
        {skill.category ? (
          <span className="rounded border border-border bg-muted/30 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            {skill.category}
          </span>
        ) : null}
      </div>
    </li>
  );
}

// Silence "unused" warnings for icons used in commented-out future code.
void ArrowDown;
void ArrowDownAZ;
void ArrowUpAZ;

export const Route = createFileRoute('/_authenticated/university/marketplace/')({
  component: MarketplaceIndexRoute,
});
